import { Box } from '@mui/material';
import React from 'react';
import { ChallengePlan } from 'src/pages/types';
import { EVENTS, trackSignupFunnel } from 'src/util/track';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setChallengePlan, setSelectedPlanId } from 'src/store/signup';
import { ChallengePlanCard } from './ChallengePackageCard';
import { isUserFromAustralia, useViewport } from 'src/lib/utils';
import { mergeChallengePlans } from '../../utils/plans.util';
import { useChallengeProducts } from '../../hooks/useChallengeProducts';

interface ChallengePlanSelectionProps {
  isChangePlanModal?: boolean;
  plans?: ChallengePlan[];
  variant?: 'package' | 'bundle';
}

export const ChallengePlanSelection: React.FC<ChallengePlanSelectionProps> = ({
  isChangePlanModal = false,
  plans,
  variant = 'package',
}) => {
  const dispatch = useAppDispatch();
  const { isMobile, isDesktop } = useViewport();

  const { allPlansSorted } = useAppSelector(state => state.signUp);

  const { challengeStartDate, challengeData } = useChallengeProducts();

  const mergedChallengePlans = mergeChallengePlans(
    allPlansSorted,
    challengeStartDate,
    challengeData?.asset_metadata
  );

  const plansToRender = plans ?? mergedChallengePlans;

  const handleSelect = (plan: ChallengePlan) => {
    trackSignupFunnel(EVENTS.signupPlanSelected, {
      plan_type:
        variant === 'bundle' ? 'challenge_bundle' : 'challenge_package',
      selected_plan_id: plan.checkoutPlanId ?? plan.id,
      displayed_plan_id: plan.id,
      selection_surface: 'challenge_plan_card',
      plan_price: plan.price,
    });
    dispatch(setChallengePlan(plan));

    if (!isChangePlanModal) {
      dispatch(setSelectedPlanId(plan.checkoutPlanId ?? plan.id));
    }
  };

  return (
    <Box
      sx={{
        ...(variant === 'bundle' && {
          width: '100%',
          maxWidth: '1200px',
          boxSizing: 'border-box',
        }),
        ...(variant === 'package' && isDesktop && { width: '1000px' }),
        ...(variant === 'package' && isMobile && { width: '320px' }),
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 3,
        justifyContent: {
          lg: 'center',
          md: 'center',
          xs: 'center',
          sm: 'flex-start',
        },
        minWidth: 0,
        ...(variant === 'package' &&
          !isUserFromAustralia() && { alignItems: 'center' }),
        ...(variant === 'package' &&
          !isUserFromAustralia() && { width: '480px' }),
      }}>
      {plansToRender.map(plan => (
        <ChallengePlanCard
          key={plan.id}
          challengePlan={plan}
          variant={variant}
          handleSelect={() => handleSelect(plan)}
        />
      ))}
    </Box>
  );
};
